import React, { memo, useCallback, useState, ChangeEvent, FormEvent } from 'react';

import { areEqual } from 'Utils/equalityChecks';
import { useSupportPanel } from 'Context/Support';

import classes from './newConversation.module.css';

function NewConversation() {
  const { categories, sendingMessage, createConversation, openConversation } = useSupportPanel();

  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [categoryId, setCategoryId] = useState<number | undefined>(undefined);
  const [error, setError] = useState('');

  const handleSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      setError('');

      if (!subject.trim()) {
        setError('Subject is required.');
        return;
      }
      if (!body.trim()) {
        setError('Message is required.');
        return;
      }

      const conv = await createConversation(subject.trim(), body.trim(), categoryId);
      if (conv) {
        openConversation(conv.id);
      } else {
        setError('Failed to create conversation. Please try again.');
      }
    },
    [subject, body, categoryId, createConversation, openConversation]
  );

  return (
    <form className={classes.container} onSubmit={handleSubmit}>
      {categories.length > 0 && (
        <div className={classes.field}>
          <label className={classes.label}>Category (optional)</label>
          <select
            className={classes.select}
            value={categoryId || ''}
            onChange={(e: ChangeEvent<HTMLSelectElement>) =>
              setCategoryId(e.target.value ? Number(e.target.value) : undefined)
            }
          >
            <option value="">Select a category...</option>
            {categories.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.name}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className={classes.field}>
        <label className={classes.label}>Subject</label>
        <input
          className={classes.input}
          type="text"
          value={subject}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setSubject(e.target.value)}
          placeholder="What do you need help with?"
        />
      </div>

      <div className={classes.field}>
        <label className={classes.label}>Message</label>
        <textarea
          className={classes.textarea}
          value={body}
          onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setBody(e.target.value)}
          placeholder="Describe your issue..."
        />
      </div>

      {error && <div className={classes.error}>{error}</div>}

      <button type="submit" className={classes.submitButton} disabled={sendingMessage}>
        {sendingMessage ? 'Sending...' : 'Send'}
      </button>
    </form>
  );
}

const NewConversationMemo = memo(NewConversation, areEqual);

export { NewConversationMemo as NewConversation };
